import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Euro,
  Inbox,
  KeyRound,
  MessageCircle,
  PenLine,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import type { UserSetupStepKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Icon, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useRequestCompanyBilling, useUserBilling } from '../../settings/billing/api';
import { ProgressBanner } from '../ui';
import { useHideChecklist, useUserChecklist, userChecklistKey } from './api';
import type { UserChecklistCard } from './types';
import { WhatsAppDialog } from './WhatsAppDialog';
import styles from './UserChecklist.module.css';

const ICONS: Record<UserSetupStepKey, typeof Euro> = {
  profile: UserRound,
  mailbox: Inbox,
  calendar: CalendarDays,
  availability: KeyRound,
  whatsapp: MessageCircle,
  signature: PenLine,
  billing: Euro,
  security: ShieldCheck,
};

/**
 * The personal checklist (Onboarding Nutzer 13–15) from `GET /setup/checklist/user`: progress banner with
 * „Ausblenden", the open steps (WhatsApp opens the Nutzer 14 dialog), done pills, first tasks; the overdue banner of
 * the payment deadline and the complete card.
 */
export function UserChecklist() {
  const checklist = useUserChecklist();
  const hide = useHideChecklist();
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const data = checklist.data;

  if (checklist.isError) {
    return (
      <Banner
        tone="danger"
        actions={
          <Button variant="secondary" size="small" onClick={() => void checklist.refetch()}>
            Erneut laden
          </Button>
        }
      >
        {errorMessage(checklist.error)}
      </Banner>
    );
  }
  if (!data) {
    return (
      <div className={styles.stack} aria-busy="true">
        <Skeleton variant="block" height={86} />
        <div className={styles.grid}>
          <Skeleton variant="block" height={150} />
          <Skeleton variant="block" height={150} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stack}>
      {data.overdue ? <OverdueBanner title={data.overdue.title} text={data.overdue.text} /> : null}
      {data.visible ? (
        <>
          <ProgressBanner
            tone="light"
            eyebrow={data.title}
            title={data.subline}
            done={data.progress.done}
            total={data.progress.total}
            onHide={() => hide.mutate()}
          />
          {hide.isError ? <Banner tone="danger">{errorMessage(hide.error)}</Banner> : null}
          {data.open.length > 0 ? (
            <div className={styles.grid}>
              {data.open.map((card, index) => (
                <OpenCard
                  key={card.key}
                  card={card}
                  primary={index === 0 && card.tone !== 'warning'}
                  onWhatsapp={() => setWhatsappOpen(true)}
                />
              ))}
            </div>
          ) : null}
          <div className={styles.row}>
            <section className={styles.card} aria-labelledby="done-title">
              <span id="done-title" className={styles.eyebrow}>
                Bereits erledigt
              </span>
              <ul className={styles.pills}>
                {data.done.map((label) => (
                  <li key={label} className={styles.pill}>
                    ✓ {label}
                  </li>
                ))}
              </ul>
            </section>
            {data.firstTasks.length > 0 ? (
              <section className={styles.card} aria-labelledby="first-title">
                <span id="first-title" className={styles.eyebrow}>
                  Ihre ersten Aufgaben
                </span>
                <ul className={styles.tasks}>
                  {data.firstTasks.map((task, index) => (
                    <li key={task.href + task.label}>
                      <Link to={task.href} className={styles.task}>
                        <span
                          className={clsx(styles.taskDot, index === 0 && styles.taskDotFirst)}
                          aria-hidden="true"
                        />
                        {task.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </>
      ) : null}
      {data.complete ? <CompleteCard done={data.progress.done} total={data.progress.total} /> : null}
      {!data.visible && !data.complete && !data.overdue ? <HiddenNote /> : null}
      <WhatsAppDialog open={whatsappOpen} onClose={() => setWhatsappOpen(false)} />
    </div>
  );
}

function OpenCard({
  card,
  primary,
  onWhatsapp,
}: {
  card: UserChecklistCard;
  primary: boolean;
  onWhatsapp: () => void;
}) {
  const navigate = useNavigate();
  const warning = card.tone === 'warning';
  return (
    <article className={clsx(styles.open, warning && styles.openWarning)}>
      <span className={clsx(styles.openIcon, warning && styles.openIconWarning)} aria-hidden="true">
        <Icon icon={ICONS[card.key]} size={16} />
      </span>
      <div className={styles.openBody}>
        <h2 className={styles.openTitle}>{card.title}</h2>
        <p className={styles.openText}>{card.text}</p>
        <button
          type="button"
          className={clsx(styles.button, primary ? styles.buttonPrimary : styles.buttonSecondary)}
          onClick={() => {
            if (card.action.key === 'whatsapp') onWhatsapp();
            else void navigate(card.action.href);
          }}
        >
          {card.action.label}
        </button>
      </div>
    </article>
  );
}

/** Nutzer 15 top: the payment deadline passed — „Jetzt hinterlegen" / „Über Firma abrechnen lassen". */
function OverdueBanner({ title, text }: { title: string; text: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const billing = useUserBilling();
  const request = useRequestCompanyBilling();
  const own = billing.data?.mode === 'own' ? billing.data : null;
  const action = own?.actions.requestCompanyBilling;
  const requested = own?.companyBillingRequestedAt ?? null;
  return (
    <section className={styles.overdue} role="alert">
      <div className={styles.overdueText}>
        <span className={styles.overdueTitle}>{title}</span>
        <span className={styles.overdueBody}>{text}</span>
        {requested || (action && !action.allowed && action.reason) ? (
          <span className={styles.overdueNote}>
            {action?.reason ?? 'Ihre Anfrage liegt bei Ihrer Administration.'}
          </span>
        ) : null}
        {request.isError ? <span className={styles.overdueNote}>{errorMessage(request.error)}</span> : null}
      </div>
      <div className={styles.overdueActions}>
        <button
          type="button"
          className={clsx(styles.button, styles.buttonPrimary)}
          onClick={() => void navigate('/settings/billing')}
        >
          Jetzt hinterlegen
        </button>
        <button
          type="button"
          className={clsx(styles.button, styles.buttonSecondary)}
          disabled={request.isPending || (action ? !action.allowed : true)}
          onClick={() =>
            request.mutate(undefined, {
              onSuccess: () => void queryClient.invalidateQueries({ queryKey: userChecklistKey }),
            })
          }
        >
          Über Firma abrechnen lassen
        </button>
      </div>
    </section>
  );
}

/** The personal checklist is hidden or was never needed (no frame — own sentence). */
function HiddenNote() {
  const navigate = useNavigate();
  return (
    <section className={styles.complete}>
      <div className={styles.completeText}>
        <h2 className={styles.completeTitle}>Keine offenen Punkte in Ihrer Checkliste.</h2>
        <p className={styles.completeBody}>Alles Weitere finden Sie in den Einstellungen.</p>
      </div>
      <button
        type="button"
        className={clsx(styles.button, styles.buttonSecondary, styles.completeButton)}
        onClick={() => void navigate('/settings/profile')}
      >
        Zu den Einstellungen
      </button>
    </section>
  );
}

/** Nutzer 15 bottom: „Einrichtung abgeschlossen · 8 von 8". */
function CompleteCard({ done, total }: { done: number; total: number }) {
  const navigate = useNavigate();
  return (
    <section className={styles.complete}>
      <span className={styles.completeIcon} aria-hidden="true">
        ✓
      </span>
      <div className={styles.completeText}>
        <h2 className={styles.completeTitle}>
          Einrichtung abgeschlossen · {done} von {total}
        </h2>
        <p className={styles.completeBody}>
          Die Checkliste verschwindet aus der Übersicht. Alles Weitere finden Sie in den Einstellungen.
        </p>
      </div>
      <button
        type="button"
        className={clsx(styles.button, styles.buttonSecondary, styles.completeButton)}
        onClick={() => void navigate('/settings/profile')}
      >
        Zu den Einstellungen
      </button>
    </section>
  );
}
